import { getCustomStylePreset, type CustomStylePreset } from './customStylePresets.ts';
import { toStyleLanguageMatchKey, normalizeStyleLanguageList } from './styleLanguageMatch.ts';

/** matchKey → custom style preset id (priority only; never mutates style bodies). */
export type StyleLanguagePriorities = Record<string, string>;

export function normalizeStyleLanguagePriorities(
  raw: unknown,
  presets: readonly CustomStylePreset[] = [],
  legacyPresetLanguages: ReadonlyArray<{ id: string; languages: unknown }> = [],
): StyleLanguagePriorities {
  const validIds = new Set(presets.map((p) => p.id));
  const out: StyleLanguagePriorities = {};

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [rawKey, rawId] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof rawId !== 'string' || !rawId.trim()) continue;
      const matchKey = rawKey === 'zh-Hans' || rawKey === 'zh-Hant'
        ? rawKey
        : toStyleLanguageMatchKey(rawKey);
      const id = rawId.trim();
      if (!matchKey || !id) continue;
      if (validIds.size > 0 && !validIds.has(id)) continue;
      if (out[matchKey]) continue;
      out[matchKey] = id;
    }
  }

  // One-time lift from legacy customStylePresets[].languages
  for (const row of legacyPresetLanguages) {
    if (validIds.size > 0 && !validIds.has(row.id)) continue;
    for (const key of normalizeStyleLanguageList(row.languages)) {
      if (!out[key]) out[key] = row.id;
    }
  }

  return out;
}

export function languagesForPreset(
  priorities: StyleLanguagePriorities,
  presetId: string,
): string[] {
  return Object.entries(priorities)
    .filter(([, id]) => id === presetId)
    .map(([key]) => key)
    .sort();
}

export function findLanguageOwnerId(
  priorities: StyleLanguagePriorities,
  matchKey: string,
  exceptId?: string,
): string | undefined {
  if (!matchKey) return undefined;
  const ownerId = priorities[matchKey];
  if (!ownerId || ownerId === exceptId) return undefined;
  return ownerId;
}

export function findLanguageOwner(
  priorities: StyleLanguagePriorities,
  presets: readonly CustomStylePreset[],
  matchKey: string,
  exceptId?: string,
): CustomStylePreset | undefined {
  const ownerId = findLanguageOwnerId(priorities, matchKey, exceptId);
  if (!ownerId) return undefined;
  return getCustomStylePreset(presets, ownerId);
}

export function setLanguagePriority(
  priorities: StyleLanguagePriorities,
  matchKey: string,
  presetId: string | null,
): StyleLanguagePriorities {
  const next = { ...priorities };
  if (!matchKey) return next;
  if (!presetId) {
    delete next[matchKey];
    return next;
  }
  next[matchKey] = presetId;
  return next;
}
